import { useState, useEffect, useRef, useContext } from 'react';
import { createPortal } from 'react-dom';
import './Portfolio.css';
import ThemeContext from './ThemeContext';

const projects = [
  {
    title: 'Style Transfer',
    shortDesc: 'This project uses the pre-trained VGG19 model and transfer learning to generate stylized images.',
    image: '/project-images/eiffel_tower.gif',
    alt: 'Style Transfer: Eiffel Tower stylized with neural network',
    fullDesc: "Neural style transfer blends the content of a photograph with the visual style of a painting or artwork. The VGG19 network's intermediate convolutional layers are used to separately extract content representations and style representations via Gram matrices. An iterative optimization process then minimizes a weighted combination of content loss and style loss, progressively transforming the input image until it matches the target style while preserving the original content.",
    tools: ['Python', 'TensorFlow', 'Keras', 'VGG19', 'NumPy', 'Matplotlib'],
    value: "Demonstrates a deep understanding of CNN feature extraction, custom loss function design, and gradient-based iterative optimization — core skills in computer vision research and production pipelines. The same technique is used in creative tools like Adobe Firefly and mobile apps like Prisma."
  },
  {
    title: 'Fruit Classification',
    shortDesc: 'Convolutional Neural Network to classify an image between 9 fruit categories. With Keras and Tensorflow.',
    image: '/project-images/fruit_classification.png',
    alt: 'Fruit Classification with CNN',
    fullDesc: "A custom CNN architecture was designed and trained from scratch to classify images across 9 fruit categories: Apple, Avocado, Banana, Cherry, Coconut, Kiwi, Lemon, Mango, and Orange. The model uses stacked convolutional and max-pooling layers followed by fully connected layers with dropout regularization. Data augmentation techniques including random rotation, horizontal flipping, and zoom were applied during training to improve generalization and reduce overfitting.",
    tools: ['Python', 'TensorFlow', 'Keras', 'NumPy', 'Matplotlib', 'scikit-learn'],
    value: "Showcases end-to-end ML workflow proficiency — from data preprocessing and augmentation to architecture design, training, evaluation, and tuning. Directly applicable to retail inventory automation, agricultural quality control, and food tech, where accurate visual classification drives significant cost savings."
  },
  {
    title: 'Transfer Learning',
    shortDesc: 'Flower type classification using InceptionV3 model with transfer learning.',
    image: '/project-images/flower_recognition.png',
    alt: 'Flower type classification using transfer learning',
    fullDesc: "This project applies transfer learning using Google's InceptionV3 model, pre-trained on ImageNet's 1.2 million images. The base model weights were frozen and a custom classification head — global average pooling, a dense ReLU layer, and a softmax output — was trained on a labeled flower dataset. A fine-tuning phase then unfroze the upper layers of InceptionV3 to further specialize the model, achieving high accuracy with a fraction of the data and compute a from-scratch model would require.",
    tools: ['Python', 'TensorFlow', 'Keras', 'InceptionV3', 'NumPy', 'Matplotlib'],
    value: "Highlights practical mastery of transfer learning and fine-tuning — a critical skill when labeled data is scarce or expensive. The identical methodology is used in medical imaging (tumor detection), satellite imagery analysis, and e-commerce product recognition at companies like Google and Amazon."
  }
];

function Portfolio() {
  const { darkMode } = useContext(ThemeContext);
  const [selected, setSelected] = useState(null);
  const [loadedImgs, setLoadedImgs] = useState({});
  const triggerRef = useRef(null);
  const modalRef = useRef(null);
  const closeRef = useRef(null);

  useEffect(() => {
    const handleKey = (e) => { if (e.key === 'Escape') setSelected(null); };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, []);

  useEffect(() => {
    if (selected) {
      document.body.style.overflow = 'hidden';
      closeRef.current?.focus();
    } else {
      document.body.style.overflow = '';
      triggerRef.current?.focus();
      triggerRef.current = null;
    }
    return () => { document.body.style.overflow = ''; };
  }, [selected]);

  // Focus trap inside modal
  useEffect(() => {
    if (!selected) return;
    const handleTrap = (e) => {
      if (e.key !== 'Tab' || !modalRef.current) return;
      const focusable = modalRef.current.querySelectorAll(
        'button, [href], input, [tabindex]:not([tabindex="-1"])'
      );
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (e.shiftKey) {
        if (document.activeElement === first) { e.preventDefault(); last.focus(); }
      } else {
        if (document.activeElement === last) { e.preventDefault(); first.focus(); }
      }
    };
    window.addEventListener('keydown', handleTrap);
    return () => window.removeEventListener('keydown', handleTrap);
  }, [selected]);

  return (
    <div className="portfolio">
      <h2>Portfolio</h2>
      <div className="project-grid">
        {projects.map((project) => (
          <div
            key={project.title}
            className="project"
            onClick={(e) => { triggerRef.current = e.currentTarget; setSelected(project); }}
            role="button"
            tabIndex={0}
            aria-label={`View details for ${project.title}`}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                triggerRef.current = e.currentTarget;
                setSelected(project);
              }
            }}
          >
            <h3>{project.title}</h3>
            <p>{project.shortDesc}</p>
            <div className="project-img-wrap">
              {!loadedImgs[project.title] && <div className="skeleton" aria-hidden="true" />}
              <img
                src={project.image}
                alt={project.alt}
                loading="lazy"
                style={{ opacity: loadedImgs[project.title] ? 1 : 0 }}
                onLoad={() => setLoadedImgs((prev) => ({ ...prev, [project.title]: true }))}
              />
            </div>
            <span className="card-hint">Learn more →</span>
          </div>
        ))}
      </div>

      {selected && createPortal(
        <div className={`modal-backdrop${darkMode ? ' dark' : ''}`} onClick={() => setSelected(null)}>
          <div
            className="modal-card"
            role="dialog"
            aria-modal="true"
            aria-labelledby="modal-title"
            ref={modalRef}
            onClick={(e) => e.stopPropagation()}
          >
            <button className="modal-close" ref={closeRef} onClick={() => setSelected(null)} aria-label="Close">✕</button>
            <img src={selected.image} alt={selected.alt} />
            <div className="modal-body">
              <h3 id="modal-title">{selected.title}</h3>
              <p className="modal-desc">{selected.fullDesc}</p>
              <div className="modal-tools">
                <span className="modal-label">Tools &amp; Technologies</span>
                <ul>
                  {selected.tools.map((t) => <li key={t}>{t}</li>)}
                </ul>
              </div>
              <div className="modal-value">
                <span className="modal-label">Value</span>
                <p>{selected.value}</p>
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}

export default Portfolio;
